import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { ChevronLeft, Loader2, Settings } from "lucide-react";
import type { NotificationCategory } from "@/api/notifications";
import { useNotifications } from "@/hooks/useNotifications";
import {
  MarkAllReadButton,
  NotificationFilters,
  NotificationRow,
  NotificationSettingsList,
} from "@/components/notifications/NotificationSections";
import { EmptyState } from "@/components/ui/EmptyState";
import { TabSelector } from "@/components/TabSelector";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-1 text-sm font-semibold text-[#d4d4d4]">
    <ChevronLeft className="size-5" />
    Back
  </button>
);

export const NotificationsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const userId = useLaunchParams().initData?.user?.id?.toString() || "";
  const [category, setCategory] = useState<NotificationCategory | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"types" | "channels">("types");
  const { data, isLoading } = useNotifications(userId, category);
  const items = data?.items ?? [];

  if (showSettings) {
    return (
      <div className="flex min-h-screen flex-col">
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-4 pb-28 [&>*]:shrink-0">
          <BackButton onClick={() => setShowSettings(false)} />
          <div>
            <h1 className="text-xl font-semibold text-white">Notification Settings</h1>
            <p className="text-sm text-[#a3a3a3]">Choose which notifications you want to receive.</p>
          </div>
          <TabSelector
            options={[
              { value: "types", label: "General" },
              { value: "channels", label: "Channels" },
            ]}
            value={settingsTab}
            onChange={setSettingsTab}
          />
          <NotificationSettingsList userId={userId} kind={settingsTab} />
        </div>
        <ReferralBottomNav />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 pb-28 [&>*]:shrink-0">
        <div className="flex items-center justify-between">
          <BackButton onClick={() => navigate(-1)} />
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#d4d4d4]"
          >
            <Settings className="size-5" />
            Settings
          </button>
        </div>
        <div>
          <h1 className="text-2xl font-semibold leading-8 text-white">Notifications</h1>
          <p className="text-base leading-6 text-[#a3a3a3]">
            Stay updated with your price alerts, trades and important activity.
          </p>
        </div>
        <NotificationFilters value={category} onChange={setCategory} mobile />
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Last Notifications</h2>
          <MarkAllReadButton userId={userId} disabled={!data?.unread} />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-8 animate-spin text-accent" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            compact
            title="You're all caught up"
            description="Price alerts, trades, listings and account updates will show up here."
          />
        ) : (
          <div className="flex flex-col gap-2">
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} userId={userId} mobile />
            ))}
          </div>
        )}
      </div>
      <ReferralBottomNav />
    </div>
  );
};
